/**
 * 店舗情報データ管理ファイル
 * 卓トレの全店舗情報を一元管理
 * バックエンドAPIから許可IPリストを取得
 */

import { configService } from '../services/configService';

// キャッシュ用の変数
let cachedAllowedIPs = null;

/**
 * 許可IPリストを初期化(アプリ起動時に1回だけ呼ぶ)
 */
export const initializeAllowedIPs = async () => {
  try {
    // configServiceがまだ初期化されていない場合
    if (!configService.isLoaded()) {
      await configService.fetchConfig();
    }
    
    const allowedIPsString = configService.getConfig('allowedIps');
    
    if (allowedIPsString) {
      cachedAllowedIPs = allowedIPsString.split(',').map(ip => ip.trim());
    } else {
      console.warn('[STORES] No allowed IPs found in config');
      cachedAllowedIPs = [];
    }
  } catch (error) {
    console.error('[STORES] Failed to initialize allowed IPs:', error);
    cachedAllowedIPs = [];
  }
};

/**
 * IPアドレスをチェックしてテスト店舗を含めるか判定
 */
const checkAllowedIP = async () => {
  try {
    // 許可IPリストが初期化されていない場合は初期化
    if (cachedAllowedIPs === null) {
      await initializeAllowedIPs();
    }
    
    // 現在のIPアドレスを取得
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    const currentIP = data.ip;
    
    const isAllowed = cachedAllowedIPs && cachedAllowedIPs.includes(currentIP);
    
    return isAllowed;
  } catch (error) {
    console.error('[STORES] Error checking IP:', error);
    return false;
  }
};

/**
 * 利用可能な店舗リストを取得(動的)
 */
export const getAvailableStores = async () => {
  const isAllowedIP = await checkAllowedIP();
  
  const allStores = [
    '選択してください',
    'テスト',
    '札幌店',
    '八戸店', 
    '仙台店',
    '山形店',
    '郡山店',
    '古河店',
    '常磐龍ケ崎店',
    '宇都宮店',
    '春日部豊春店',
    '西川口店',
    '船橋店',
    '王子神谷店',
    '葛西店',
    '金町店',
    '世田谷経堂店',
    '高田馬場店',
    '日本橋店',
    '練馬中村橋店',
    '八王子店',
    '府中店',
    '町田店',
    '目白店',
    '川崎宮前平店',
    '中央林間店',
    '相模原店',
    '横浜上大岡店',
    '横浜東神奈川店',
    '西中島南方店',
    'あびこ店'
  ];
  
  // 許可されたIPでなければテスト店舗を除外
  if (!isAllowedIP) {
    return allStores.filter(store => store !== 'テスト');
  }
  return allStores;
};

export const stores = [
  '選択してください',
  // TODO 開発用
  //'テスト',
  '札幌店',
  '八戸店', 
  '仙台店',
  '山形店',
  '郡山店',
  '古河店',
  '常磐龍ケ崎店',
  '宇都宮店',
  '春日部豊春店',
  '西川口店',
  '船橋店',
  '王子神谷店',
  '葛西店',
  '金町店',
  '世田谷経堂店',
  '高田馬場店',
  '日本橋店',
  '練馬中村橋店',
  '八王子店',
  '府中店',
  '町田店',
  '目白店',
  '川崎宮前平店',
  '中央林間店',
  '相模原店',
  '横浜上大岡店',
  '横浜東神奈川店',
  '西中島南方店',
  'あびこ店'
];

/**
 * 店舗固有の設定情報
 */
export const storeSettings = {
  // 新規受付を停止している店舗
  suspendedStores: ['御徒町店'],
  
  // シューズロッカーがない店舗
  noShoeLockerStores: [
    '常磐龍ケ崎店', '春日部豊春店', '八王子店', '王子神谷店',
    '金町店', '郡山店', '仙台店', '船橋店','宇都宮店','相模原店'
  ],

  // 店舗レイアウトが利用可能な店舗
  storeLayoutAvailableStores: [
    '金町店',
    // TODO 開発用
    'テスト',
    '西川口店',
    '葛西店',
    '世田谷経堂店',
    '練馬中村橋店',
    '古河店'
  ],
  
  // スマートロック設置店舗
  smartLockStores: [
    '世田谷経堂店',
    '西川口店',
    '練馬中村橋店',
    '葛西店',
    '常磐龍ケ崎店',
    '横浜東神奈川店',
    '高田馬場店',
    '目白店',
    '中央林間店',
    '宮前平店',
    '春日部豊春店',
    '金町店',
    // TODO 開発用
    'テスト',
    '八王子店',
    '王子神谷店',
    '町田店',
    '仙台店',
    '横浜上大岡店',
    '日本橋店',
    '船橋店',
    '山形店',
    '八戸店',
    '札幌店',
    '古河店',
    '府中店',
    '西中島南方店',
    '郡山店',
    'あびこ店',
    '宇都宮店',
    '相模原店'
  ],

  // スマートロックマニュアルが利用可能な店舗（ALSOKを除外）
  smartLockManualAvailableStores: [
    '横浜東神奈川店',
    '高田馬場店',
    '目白店',
    '中央林間店',
    '川崎宮前平店',
    '春日部豊春店',
    '金町店',
    '八王子店',
    '王子神谷店',
    '町田店',
    '仙台店',
    '横浜上大岡店',
    '日本橋店',
    '船橋店',
    '山形店',
    '八戸店',
    '札幌店',
    '古河店',
    '府中店',
    '西中島南方店',
    '郡山店',
    'あびこ店',
    '宇都宮店',
    '相模原店'
  ],

  // 地域別グループ（将来の機能拡張用）
  regions: {
    hokkaido: ['札幌店'],
    tohoku: ['八戸店', '仙台店', '山形店', '郡山店'],
    kanto: [
      '古河店', '常磐龍ケ崎店', '春日部豊春店', '西川口店', '船橋店',
      '王子神谷店', '葛西店', '金町店', '世田谷経堂店', '高田馬場店',
      '日本橋店', '練馬中村橋店', '八王子店', '府中店', '町田店',
      '目白店','宇都宮店'
    ],
    kanagawa: ['川崎宮前平店', '中央林間店', '相模原店', '横浜上大岡店', '横浜東神奈川店'],
    kansai: ['西中島南方店', 'あびこ店']
  }
};

/**
 * 店舗別初期費用・鍵情報マッピング
 */
export const storeInitialCosts = {
  // TODO 開発用
  'テスト': { cost: 10, keyType: 'Akerun' },
  '世田谷経堂店': { cost: 4840, keyType: 'ALSOK' },
  '西川口店': { cost: 4840, keyType: 'ALSOK' },
  '練馬中村橋店': { cost: 4840, keyType: 'ALSOK' },
  '葛西店': { cost: 4840, keyType: 'ALSOK' },
  '常磐龍ケ崎店': { cost: 4840, keyType: 'ALSOK' },
  '横浜東神奈川店': { cost: 4840, keyType: 'Akerun' },
  '府中店': { cost: 4840, keyType: 'NinjaLock' },
  '高田馬場店': { cost: 4840, keyType: 'Akerun' },
  '西中島南方店': { cost: 4400, keyType: 'NinjaLock' },
  '目白店': { cost: 4840, keyType: 'Akerun' },
  '中央林間店': { cost: 4840, keyType: 'Akerun' },
  '川崎宮前平店': { cost: 4840, keyType: 'Akerun' },
  '春日部豊春店': { cost: 4400, keyType: 'Akerun' },
  '金町店': { cost: 4840, keyType: 'Akerun' },
  '八王子店': { cost: 4840, keyType: 'Akerun' },
  '王子神谷店': { cost: 5280, keyType: 'Akerun' },
  '町田店': { cost: 4840, keyType: 'Akerun' },
  '郡山店': { cost: 4400, keyType: 'NinjaLock' },
  '仙台店': { cost: 4400, keyType: 'Akerun' },
  '横浜上大岡店': { cost: 5280, keyType: 'Akerun' },
  '日本橋店': { cost: 4840, keyType: 'Akerun' },
  '船橋店': { cost: 4840, keyType: 'Akerun' },
  '山形店': { cost: 4400, keyType: 'Akerun' },
  '八戸店': { cost: 4400, keyType: 'Akerun' },
  '札幌店': { cost: 4400, keyType: 'Akerun' },
  '古河店': { cost: 4400, keyType: 'Akerun' },
  'あびこ店': { cost: 4400, keyType: 'Akerun' },
  '宇都宮店': { cost: 4400, keyType: 'Akerun' },
  '相模原店': { cost: 4840, keyType: 'Akerun' }
};

/**
 * 初期費用設定の定数
 */
export const initialCostSettings = {
  // 料金体系別の詳細情報
  pricingTiers: {
    4400: {
      keyFee: 3300,
      monthlyFee: 1100,
      description: '鍵発行費3,300円＋初月会費1,100円'
    },
    4840: {
      keyFee: 3630,
      monthlyFee: 1210,
      description: '鍵発行費3,630円＋初月会費1,210円'
    },
    5280: {
      keyFee: 3960,
      monthlyFee: 1320,
      description: '鍵発行費3,960円＋初月会費1,320円'
    },
    // TODO　開発用
    // テスト用 
    10: {
      keyFee: 5,
      monthlyFee: 5,
      description: '鍵発行費5円＋初月会費5円'
    }
  },

  // デフォルト設定（店舗が見つからない場合）
  default: {
    cost: 4840,
    keyFee: 3630,
    monthlyFee: 1210,
    description: '鍵発行費3,630円＋初月会費1,210円'
  }
};

/**
 * 店舗検索・フィルタリング用のヘルパー関数
 */
export const storeUtils = {
  /**
   * 値から店舗名を取得（現在は値と店舗名が同一）
   * @param {string} value - 店舗のvalue値（店舗名）
   * @returns {string} 店舗名
   */
  getStoreName: (value) => {
    const store = stores.find(s => s.value === value);
    return store ? store.label : '';
  },

  /**
   * 店舗名から値を取得（現在は値と店舗名が同一）
   * @param {string} label - 店舗名
   * @returns {string} 店舗のvalue値
   */
  getStoreValue: (label) => {
    const store = stores.find(s => s.label === label);
    return store ? store.value : '';
  },

  /**
   * 地域別店舗の取得
   * @param {string} region - 地域名
   * @returns {Array} 該当地域の店舗リスト
   */
  getStoresByRegion: (region) => {
    const regionStores = storeSettings.regions[region] || [];
    return stores.filter(store => regionStores.includes(store.label));
  },

  /**
   * シューズロッカーが利用可能な店舗かチェック
   * @param {string} storeName - 店舗名
   * @returns {boolean} 利用可能かどうか
   */
  hasShoeLocker: (storeName) => {
    return !storeSettings.noShoeLockerStores.includes(storeName);
  },

  /**
   * 店舗レイアウトが利用可能な店舗かチェック
   * @param {string} storeName - 店舗名
   * @returns {boolean} 利用可能かどうか
   */
  hasStoreLayout: (storeName) => {
    return storeSettings.storeLayoutAvailableStores.includes(storeName);
  },

  /**
   * スマートロックが利用可能な店舗かチェック
   * @param {string} storeName - 店舗名
   * @returns {boolean} 利用可能かどうか
   */
  hasSmartLock: (storeName) => {
    return storeSettings.smartLockStores.includes(storeName);
  },

  /**
   * スマートロックマニュアルが利用可能な店舗かチェック
   * @param {string} storeName - 店舗名
   * @returns {boolean} 利用可能かどうか
   */
  hasSmartLockManual: (storeName) => {
    return storeSettings.smartLockManualAvailableStores.includes(storeName);
  },

  /**
   * 新規受付可能な店舗のみフィルタ
   * @returns {Array} 新規受付可能な店舗リスト
   */
  getAvailableStores: () => {
    return stores.filter(store => 
      !storeSettings.suspendedStores.includes(store.label)
    );
  },

  /**
   * 店舗の初期費用情報を取得
   * @param {string} storeName - 店舗名
   * @returns {Object} 初期費用情報
   */
  getStoreCostInfo: (storeName) => {
    const costInfo = storeInitialCosts[storeName];
    if (!costInfo) {
      return initialCostSettings.default;
    }

    const tierInfo = initialCostSettings.pricingTiers[costInfo.cost];
    return {
      cost: costInfo.cost,
      keyType: costInfo.keyType,
      keyFee: tierInfo.keyFee,
      monthlyFee: tierInfo.monthlyFee,
      description: tierInfo.description
    };
  },

  /**
   * 店舗の鍵タイプを取得
   * @param {string} storeName - 店舗名
   * @returns {string} 鍵タイプ
   */
  getStoreKeyType: (storeName) => {
    return storeInitialCosts[storeName]?.keyType || 'Akerun';
  }
};